import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eyebrow, Pill, SectionStamp } from '../components/Atoms';
import Icon from '../components/Icon';
import DesktopHeader from '../components/DesktopHeader';
import DesktopFooter from '../components/DesktopFooter';
import DesktopWhatsAppFab from '../components/DesktopWhatsAppFab';
import { fonts } from '../theme';

const CARE = [
  { ic: 'spine', n: '01', title: 'Orthopaedic', desc: 'Joint pain, sports & post-injury rehabilitation.' },
  { ic: 'brain', n: '02', title: 'Neurological', desc: 'Stroke, MS and Parkinson’s recovery plans.' },
  { ic: 'baby', n: '03', title: 'Paediatric', desc: 'Developmental, congenital and movement care.' },
  { ic: 'cane', n: '04', title: 'Geriatric', desc: 'Balance, falls prevention and strength.' },
  { ic: 'bandage', n: '05', title: 'Post-op rehab', desc: 'Knee, hip, shoulder — paced to your healing.' },
  { ic: 'home', n: '+', title: 'Home visits', desc: 'Same care, in your living room.', mint: true },
];

const STATS = [
  { k: '4+', v: 'years of clinical practice' },
  { k: '5', v: 'specialties under one roof' },
  { k: 'Home', v: 'visits across Vadodara' },
  { k: '24h', v: 'reply on WhatsApp' },
];

export default function HomeDesktop({ t }) {
  const navigate = useNavigate();

  return (
    <div style={{
      width: '100%', minHeight: '100vh', background: t.paper, color: t.ink,
      fontFamily: fonts.sans, position: 'relative', overflow: 'hidden',
    }}>
      <DesktopHeader t={t} />

      <div style={{
        display: 'grid', gridTemplateColumns: '1.2fr 1fr', minHeight: 620,
        background: t.ink, color: t.paper,
      }}>
        <div style={{
          padding: '80px max(56px, 4vw) 56px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <Eyebrow t={t} color={t.mint}>Physiotherapy · Vadodara · Est. 2021</Eyebrow>
            <h1 style={{
              fontFamily: fonts.serif, fontWeight: 300,
              fontSize: 'clamp(72px, 10vw, 128px)', lineHeight: 0.92,
              letterSpacing: '-0.04em', margin: '32px 0 0', color: t.paper,
            }}>
              Move<br/>
              <span style={{ fontStyle: 'italic', color: t.mint, fontWeight: 400 }}>better,</span><br/>
              heal slower.
            </h1>
          </div>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 32, flexWrap: 'wrap', marginTop: 48,
          }}>
            <p style={{
              fontFamily: fonts.sans, fontSize: 16, lineHeight: 1.55,
              color: 'rgba(246,243,236,0.72)', maxWidth: 420, margin: 0,
            }}>
              Hands-on physiotherapy by{' '}
              <em style={{ fontStyle: 'italic', color: t.mint }}>Dr. Moksha Patel (PT)</em>{' '}
              — orthopaedic, neuro, paediatric &amp; post-op care, at the clinic or in your home.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <Pill t={t} fill={t.mint} ink={t.ink} icon="calendar" onClick={() => navigate('/book')}>
                Book
              </Pill>
              <Pill
                t={t} fill="transparent" ink={t.paper}
                style={{ border: `1px solid rgba(255,255,255,0.2)` }}
                onClick={() => navigate('/about')}
              >
                Read more
              </Pill>
            </div>
          </div>
        </div>
        <div style={{ position: 'relative', overflow: 'hidden', minHeight: 480 }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(60% 70% at 50% 30%, ${t.tint}99, ${t.ink})`,
          }}/>
          <img src={`${process.env.PUBLIC_URL}/assets/hero.webp`} alt="Dr. Moksha Patel" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'contain', objectPosition: 'center bottom',
          }}/>
          <div style={{
            position: 'absolute', left: 32, top: 32, color: t.paper,
            fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.18em',
            textTransform: 'uppercase', opacity: 0.8,
          }}>
            GPC-21250 · License
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        padding: '40px max(56px, 4vw)', borderBottom: `0.5px solid ${t.hair}`,
        maxWidth: 1440, margin: '0 auto',
      }}>
        {STATS.map((s, i) => (
          <div key={i} style={{
            borderLeft: i ? `0.5px solid ${t.hair}` : 'none',
            paddingLeft: i ? 32 : 0,
          }}>
            <div style={{
              fontFamily: fonts.serif, fontSize: 'clamp(40px, 4vw, 56px)', fontWeight: 300,
              lineHeight: 1, letterSpacing: '-0.035em',
            }}>{s.k}</div>
            <div style={{ fontFamily: fonts.sans, fontSize: 13, color: t.muted, marginTop: 10 }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '80px max(56px, 4vw)', maxWidth: 1440, margin: '0 auto' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: 40, flexWrap: 'wrap', gap: 16,
        }}>
          <div>
            <SectionStamp n="01" label="Care" t={t}/>
            <h2 style={{
              fontFamily: fonts.serif, fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 300,
              letterSpacing: '-0.035em', lineHeight: 1.02, margin: '20px 0 0', maxWidth: 700,
              textWrap: 'balance',
            }}>
              Five practices, <span style={{ fontStyle: 'italic' }}>one</span> patient-first method.
            </h2>
          </div>
          <Pill
            t={t} fill="transparent" ink={t.ink}
            style={{ border: `1px solid ${t.hair}` }}
            onClick={() => navigate('/services')}
          >
            All specialties
          </Pill>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {CARE.map((c, i) => (
            <div key={i} style={{
              padding: 28, borderRadius: 18,
              background: c.mint ? t.mint : 'transparent',
              color: t.ink,
              border: c.mint ? 'none' : `0.5px solid ${t.hair}`,
              minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => navigate(c.mint ? '/contact' : '/services')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Icon name={c.ic} size={28} sw={1.4}/>
                <span style={{
                  fontFamily: fonts.mono, fontSize: 11,
                  color: c.mint ? t.mintDeep : t.muted, letterSpacing: '0.16em',
                }}>{c.n}</span>
              </div>
              <div>
                <div style={{
                  fontFamily: fonts.serif, fontSize: 28, fontWeight: 400, letterSpacing: '-0.02em',
                }}>{c.title}</div>
                <div style={{
                  fontFamily: fonts.sans, fontSize: 13,
                  color: c.mint ? t.ink : t.muted, marginTop: 8, lineHeight: 1.45,
                }}>{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <DesktopFooter t={t} />
      <DesktopWhatsAppFab />
    </div>
  );
}
